/**
 * Unity 준비 상태 확인 모듈
 */

const isDev = process.env.NODE_ENV === "development";

let unityReadyState = false;
let unityLoadedCheck = false;
let unityIframe: HTMLIFrameElement | null = null;
/** iframe에 붙인 load 리스너 참조 — resetUnityState 시 제거하여 detached iframe이 GC되도록 함 */
let unityIframeLoadHandler: (() => void) | null = null;
let iframeLoadAttempts = 0;
const MAX_LOAD_ATTEMPTS = 30; // 최대 15초 대기 (500ms * 30)

// 디버깅 로그 스팸 방지
let lastDebugLogTime = 0;
const DEBUG_LOG_INTERVAL = 2000; // 2초마다만 디버깅 로그 출력

/**
 * Unity iframe인지 확인하는 헬퍼 함수
 */
function isUnityIframe(iframe: HTMLIFrameElement | null): boolean {
  if (!iframe) return false;

  const title = iframe.title?.toLowerCase() || "";
  if (title.includes("unity")) return true;

  const src = iframe.src?.toLowerCase() || "";
  if (src.includes("unity")) return true;

  const id = iframe.id?.toLowerCase() || "";
  if (id.includes("unity")) return true;

  return false;
}

/**
 * iframe 로딩 상태 체크 함수
 */
function isIframeReady(iframe: HTMLIFrameElement | null): boolean {
  if (!iframe) return false;

  // iframe에 src가 있으면 준비된 것으로 간주
  if (iframe.src) {
    return true;
  }

  // 외부 도메인 iframe 처리
  if (iframe.src) {
    try {
      const iframeUrl = new URL(iframe.src, window.location.origin);
      const currentUrl = new URL(window.location.href);

      // 다른 도메인의 iframe인 경우 CORS 제한으로 contentWindow 접근 불가
      if (iframeUrl.hostname !== currentUrl.hostname) {
        return true; // 외부 도메인은 contentWindow 없이도 준비된 것으로 간주
      }
    } catch (e) {
      return true; // URL 파싱에 실패해도 src가 있으면 준비된 것으로 간주
    }
  }

  // contentWindow가 있는지 확인
  if (!iframe.contentWindow) {
    return false;
  }

  // contentDocument가 있는지 확인 (추가 검증) - CORS 오류 무시
  try {
    if (iframe.contentDocument && iframe.contentDocument.readyState) {
      if (iframe.contentDocument.readyState !== "complete") {
        return false;
      }
    }
  } catch (e) {
    // CORS 정책으로 contentDocument 접근이 불가능한 경우
    // CORS 오류가 있어도 contentWindow가 있으면 준비된 것으로 간주
    return true;
  }

  // readyState가 'complete'인지 확인
  if (
    iframe.contentDocument?.readyState &&
    iframe.contentDocument.readyState !== "complete"
  ) {
    return false;
  }

  return true;
}

/**
 * Unity 준비 상태 확인
 */
export function isUnityReady(): boolean {
  // 먼저 Unity iframe을 찾기
  if (!unityIframe) {
    unityIframe = getUnityInstance();
  }

  // iframe이 없으면 준비되지 않은 것으로 간주
  if (!unityIframe) {
    return false;
  }

  // iframe이 DOM에 없으면 준비되지 않은 것으로 간주
  if (!unityIframe.isConnected || !document.contains(unityIframe)) {
    unityIframe = null;
    unityReadyState = false;
    unityLoadedCheck = false;
    return false;
  }

  // Unity iframe인지 확인 (안전장치)
  if (!isUnityIframe(unityIframe)) {
    unityIframe = null;
    unityReadyState = false;
    unityLoadedCheck = false;
    return false;
  }

  // iframe이 있고 contentWindow가 있으면 강제로 준비 상태로 설정
  if (unityIframe && unityIframe.contentWindow && !unityReadyState) {
    setUnityReady(true);
    unityLoadedCheck = true;
  }

  // 외부 도메인 iframe 처리 - contentWindow가 없어도 준비된 것으로 간주
  if (unityIframe && unityIframe.src && !unityIframe.contentWindow) {
    try {
      const iframeUrl = new URL(unityIframe.src, window.location.origin);
      const currentUrl = new URL(window.location.href);

      // 다른 도메인의 iframe인 경우 CORS 제한으로 contentWindow 접근 불가
      if (iframeUrl.hostname !== currentUrl.hostname) {
        if (!unityReadyState) {
          setUnityReady(true);
          unityLoadedCheck = true;
        }
      } else {
        // 같은 도메인이지만 contentWindow가 없는 경우도 준비된 것으로 간주
        if (!unityReadyState) {
          setUnityReady(true);
          unityLoadedCheck = true;
        }
      }
    } catch (urlError) {
      console.error("❌ URL 파싱 오류:", urlError);
      // URL 파싱에 실패해도 iframe이 있고 src가 있으면 준비된 것으로 간주
      if (!unityReadyState) {
        setUnityReady(true);
        unityLoadedCheck = true;
      }
    }
  }

  // 더 강력한 fallback: iframe이 존재하고 src가 설정되어 있으면 준비된 것으로 간주
  if (unityIframe && unityIframe.src && !unityReadyState && !unityLoadedCheck) {
    setUnityReady(true);
    unityLoadedCheck = true;
  }

  // contentWindow가 없지만 iframe이 존재하는 경우 - 로딩 시도
  if (
    unityIframe &&
    !unityIframe.contentWindow &&
    iframeLoadAttempts < MAX_LOAD_ATTEMPTS
  ) {
    iframeLoadAttempts++;

    // 주기적으로 contentWindow 확인
    setTimeout(() => {
      if (unityIframe && unityIframe.contentWindow) {
        setUnityReady(true);
        unityLoadedCheck = true;
      }
    }, 500);
  }

  const ready =
    (unityReadyState || unityLoadedCheck) &&
    unityIframe &&
    isIframeReady(unityIframe);

  // 디버깅 정보 제공 (스팸 방지)
  if (!ready) {
    const now = Date.now();
    if (now - lastDebugLogTime > DEBUG_LOG_INTERVAL) {
      lastDebugLogTime = now;
    }
  }

  return ready;
}

/**
 * Unity 준비 상태 설정 (중복 호출 방지)
 */
export function setUnityReady(ready: boolean): void {
  // 이미 준비 상태면 중복 설정 방지
  if (ready && unityReadyState) {
    return;
  }

  if (isDev) console.log(`✅ Unity 준비 상태 설정: ${ready}`);
  unityReadyState = ready;

  // Unity가 준비되면 iframe도 찾아서 설정
  if (ready && !unityIframe) {
    unityIframe = getUnityInstance();
  }

  // 준비 완료되면 iframeLoadAttempts 리셋
  if (ready) {
    iframeLoadAttempts = 0;
  }

  // 이벤트 발생
  window.dispatchEvent(new CustomEvent("unity:ready", { detail: { ready } }));
}

/**
 * Unity iframe 인스턴스 가져오기
 */
export function getUnityInstance(): HTMLIFrameElement | null {
  // 캐시된 iframe이 DOM에서 제거되었거나 Unity iframe이 아니면 무효화
  if (unityIframe) {
    const isInDOM = unityIframe.isConnected && document.contains(unityIframe);
    const isUnity = isUnityIframe(unityIframe);

    if (!isInDOM || !isUnity) {
      unityIframe = null;
      // iframe이 제거되었으면 상태도 리셋
      if (!isInDOM) {
        unityReadyState = false;
        unityLoadedCheck = false;
      }
    }
  }

  if (!unityIframe) {
    // 여러 가지 방법으로 Unity iframe 찾기
    const candidates = [
      document.querySelector<HTMLIFrameElement>('iframe[title="Unity"]'),
      document.querySelector<HTMLIFrameElement>(
        'iframe[title="Unity WebGL Application"]'
      ),
      document.querySelector<HTMLIFrameElement>('iframe[id="unity-iframe"]'),
      document.querySelector<HTMLIFrameElement>('iframe[src*="unity"]'),
    ];

    // Unity iframe인지 확인하면서 찾기
    for (const candidate of candidates) {
      if (candidate && isUnityIframe(candidate)) {
        unityIframe = candidate;
        break;
      }
    }

    if (unityIframe) {
      if (isDev) console.log("✅ Unity iframe 찾음:", unityIframe.src);

      // iframe 로딩 완료 이벤트 리스너 추가
      if (!unityIframe.hasAttribute("data-loading-listener-added")) {
        unityIframe.setAttribute("data-loading-listener-added", "true");

        unityIframeLoadHandler = () => {
          iframeLoadAttempts = 0; // 로드 완료 시 카운터 리셋
          // contentWindow 확인을 위한 지연 처리
          setTimeout(() => {
            if (unityIframe && unityIframe.contentWindow) {
              setUnityReady(true);
              unityLoadedCheck = true;
            } else {
              iframeLoadAttempts = 0;
            }
          }, 100);
        };
        unityIframe.addEventListener("load", unityIframeLoadHandler);

        // 이미 로드된 경우
        if (unityIframe.contentWindow) {
          setUnityReady(true);
          unityLoadedCheck = true;
        }
      }
    } else if (isDev) {
      console.warn("⚠️ Unity iframe을 찾을 수 없음");
    }
  }
  return unityIframe;
}

/**
 * Unity 상태 리셋 (EDITOR 언마운트 시 호출 — iframe 참조·리스너 제거로 GC 유도)
 *
 * about:blank 설정은 하지 않음. EDITOR unmount 시에는 UNLOAD_QUIT_DELAY_MS 후
 * iframe.src/remove로 처리하고, Space remount 시에는 setIframeKey으로 React가
 * 이전 iframe을 교체함. 즉시 about:blank를 설정하면 Unity가 Quit 메시지를
 * 처리하기 전에 컨텍스트가 파괴되어 WebGL/WASM 메모리가 해제되지 않을 수 있음.
 */
export function resetUnityState(): void {
  if (unityIframe) {
    if (unityIframeLoadHandler) {
      try {
        unityIframe.removeEventListener("load", unityIframeLoadHandler);
      } catch (_) {
        // ignore
      }
      unityIframeLoadHandler = null;
    }
    // about:blank 설정 제거 — Quit 처리 대기 후 EDITOR cleanup 또는 React unmount에서 처리
  }
  unityReadyState = false;
  unityLoadedCheck = false;
  unityIframe = null;
  iframeLoadAttempts = 0;
}

/**
 * iframe 로드 시도 횟수 리셋
 */
export function resetIframeLoadAttempts(): void {
  iframeLoadAttempts = 0;
}
